import { t } from 'i18next';
import { Combiner } from '../../../common/components/FilterPanel/components/Combiner';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { Operator } from '../../../common/components/FilterPanel/enum/Operator';
import { FilterConfiguration, Option } from '../../../common/components/FilterPanel/types';
import { getUniqueId } from '../../../common/components/FilterPanel/utils';
import { PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { TlsHandshakesTableController } from '../../../common/handshakes/controllers/TlsHandshakesTableController';
import { TlsServerVersion, formatTlsServerVersion, formatTlsServerVersionDecimal } from '../../../common/handshakes/enums/TlsServerVersion';
import { TlsHandshake } from '../../../common/handshakes/models/TlsHandshake';
import { TlsHandshakesRepository } from '../../../common/handshakes/services/TlsHandshakesRepository';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { portNumber } from '../../../common/hooks/use-form-item/validators/network';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { ReportCiphersuitesRepository } from '../../ciphersuites/services/ReportCiphersuitesRepository';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone, ReportFailed, ReportPending } from '../../common/models/Report';

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = PaginatedTableState & {};

type CiphersuiteFilter = {
  id: string;
  operation: Operation;
};

const CIPHERSUITE_FILTER = 'ciphersuite';

export class Controller extends TlsHandshakesTableController<Props> {
  private readonly handshakesRepository = new TlsHandshakesRepository();
  private readonly reportCiphersuitesRepository = new ReportCiphersuitesRepository();

  // Store both selected and supported ciphersuites: the effective list used in the `ciphersuite`
  // filter depends on which type is requested via query params
  private selectedCiphersuites: Option[] = [];
  private supportedCiphersuites: Option[] = [];
  private filterCiphersuites: Option[] = [];

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.handshakesRepository.cancelAllRequests();
    this.reportCiphersuitesRepository.cancelAllRequests();
  }

  protected doInitialDataFetch() {
    // nothing, we aren't fetching anything until we receive a ReportDone from context
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<TlsHandshake>> {
    const { report } = this.props.context;

    if (!(report instanceof ReportDone) || report.getTlsHandshakesCount() === 0) return BLANK_PAGINATION_RESULT;

    try {
      return await this.handshakesRepository.getList(ModelType.Report, report.getId(), cursorInfo, this.getFilters(), this.getSorters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected getInitialFilters(): Combiner | null {
    // If we have ciphersuite filter passed in, we will replace the stored filters with new filter value.
    // By doing that, we can do a subsequent visit to the page and have the matching stored filter.
    const ciphersuiteFilter = this.getCiphersuiteFilter();
    if (ciphersuiteFilter !== null) {
      // setup internal state and save that to the url, replacing the current structure
      this.filters = ciphersuiteFilter;

      this.storeFiltersInUrl(true);
    }

    return super.getInitialFilters();
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    const filters: FilterConfiguration[] = [];
    if (!(this.props.context.report instanceof ReportDone)) {
      return filters;
    }
    if (this.filterCiphersuites.length) {
      filters.push({
        filterKey: CIPHERSUITE_FILTER,
        label: t('handshakes.ciphersuite'),
        type: FilterValueInputType.SELECT,
        options: this.filterCiphersuites.map((o) => ({ label: o.label ?? t('unknownCiphersuite', { nid: o.value }), value: o.value })),
        operations: [
          { value: Operation.EQ, label: t('filterLabels.isSelected') },
          { value: Operation.CONTAINS, label: t('filterLabels.isSupported') },
        ],
      });
    }
    filters.push(
      {
        filterKey: 'initiator_ip',
        label: t('handshakes.sourceIp'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'target_ip',
        label: t('handshakes.targetIp'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'initiator_port',
        label: t('handshakes.sourcePort'),
        type: FilterValueInputType.STRING,
        validator: () => portNumber(),
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'target_port',
        label: t('handshakes.targetPort'),
        type: FilterValueInputType.STRING,
        validator: () => portNumber(),
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'timestamp',
        label: t('handshakes.timestamp'),
        type: FilterValueInputType.TIMESTAMP,
        operations: [
          { value: Operation.LT, label: t('filterLabels.isBefore') },
          { value: Operation.LE, label: t('filterLabels.isBeforeOrEqual') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.GE, label: t('filterLabels.isAfterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isAfter') },
        ],
      },
      {
        filterKey: 'server_name_indication',
        label: t('handshakes.serverName'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'server_chosen_version',
        label: t('handshakes.version'),
        type: FilterValueInputType.SELECT,
        options: Object.values(TlsServerVersion)
          .filter((value) => value !== TlsServerVersion.UNKNOWN)
          .map((value) => ({ value: formatTlsServerVersionDecimal(value), label: formatTlsServerVersion(value) })),
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
      }
    );
    return filters;
  }

  private getCiphersuiteFilter(): Combiner | null {
    const ciphersuiteFilter = this.fetchCiphersuiteFiltersFromQueryParams();
    // Update the filter ciphersuites based on the applied filter
    this.setCiphersuiteFilterOptions(ciphersuiteFilter);

    // No filter applied, or selection is invalid
    if (!ciphersuiteFilter || !this.validateCiphersuiteFilter(ciphersuiteFilter)) {
      return null;
    }

    return {
      id: getUniqueId(),
      operator: Operator.AND,
      subFilters: [
        {
          id: getUniqueId(),
          operator: Operator.AND,
          subFilters: [
            {
              id: getUniqueId(),
              operation: ciphersuiteFilter.operation,
              value: ciphersuiteFilter.id,
              field: CIPHERSUITE_FILTER,
            },
          ],
        },
      ],
    };
  }

  private fetchCiphersuiteFiltersFromQueryParams(): CiphersuiteFilter | null {
    const searchParams = this.props.searchParams;
    const ciphersuiteId = searchParams.get('selected_ciphersuite') ?? searchParams.get('supported_ciphersuite');
    if (!ciphersuiteId) {
      return null;
    }

    const operation = searchParams.has('selected_ciphersuite') ? Operation.EQ : Operation.CONTAINS;
    return {
      operation,
      id: ciphersuiteId,
    };
  }

  private setCiphersuiteFilterOptions(filter: CiphersuiteFilter | null) {
    // By default, only show the selected ciphersuites
    this.filterCiphersuites = this.selectedCiphersuites;
    if (!filter || filter.operation === Operation.EQ) {
      return;
    }
    // A "is supported" filter is applied, show the selected ciphersuite plus the requested one
    const supportedInSelected = this.selectedCiphersuites.some((c) => c.value === filter.id);
    if (supportedInSelected) {
      // The requested ciphersuite is also selected, only show the selected
      return;
    }
    const supported = this.supportedCiphersuites.find((c) => c.value === filter.id);
    if (supported) {
      // The requested is not selected, prepend it to the options
      this.filterCiphersuites = [supported, ...this.selectedCiphersuites];
      return;
    }
  }

  private validateCiphersuiteFilter(filter: CiphersuiteFilter | null) {
    if (!filter) return true;

    const selectedExists = this.selectedCiphersuites.some((c) => c.value === filter.id);
    const supportedExists = this.supportedCiphersuites.some((c) => c.value === filter.id);

    if (filter.operation === Operation.EQ && !selectedExists) {
      return false;
    }
    if (filter.operation === Operation.CONTAINS && !supportedExists) {
      return false;
    }
    return true;
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed (e.g. through a version selector)
      // Reset the table state (including stored data and pagination) and reload
      this.resetState(() => this.loadReportContext());
      return;
    }

    if (!currReport || currReport instanceof ReportPending) {
      // The report is not ready yet, display the initial loading state
      this.resetState();
    } else if (currReport instanceof ReportFailed) {
      // The report is failed, display the blank loaded state
      this.setupBlankState();
    } else if (currReport instanceof ReportDone) {
      // The report is done, load the "presets" since they will trigger the rest.
      this.fetchCiphersuites(currReport);
    }
  }

  private async fetchCiphersuites(report: ReportDone) {
    try {
      if (report.getTlsHandshakesCount() === 0) {
        // The report has no handshakes, avoid fetching ciphersuites
        super.doInitialDataFetch();
        return;
      }
      const { selected, supported } = await this.reportCiphersuitesRepository.getForFilters(report.getId());
      this.selectedCiphersuites = selected;
      this.supportedCiphersuites = supported;

      super.doInitialDataFetch();
    } catch (e) {
      this.setState({ loadingData: false, hadAnyData: false });
      this.handleError(e, { invalidUrl: t('nonExistingReport') });
    }
  }
}

export const ReportTlsHandshakesController = withRouter<ReportOutletContextType>(Controller);
